import { supabase } from '../lib/supabase';

const getSupabaseUrl = () => import.meta.env?.VITE_SUPABASE_URL || '';
const getAnonKey = () => import.meta.env?.VITE_SUPABASE_ANON_KEY || '';

const callEdgeFunction = async (functionName, body) => {
  const supabaseUrl = getSupabaseUrl();
  if (!supabaseUrl) return { success: false, error: 'Supabase URL not configured' };
  try {
    let token = getAnonKey();
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (session?.access_token) token = session?.access_token;
    } catch (e) {}
    const response = await fetch(`${supabaseUrl}/functions/v1/${functionName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'apikey': getAnonKey()
      },
      body: JSON.stringify(body)
    });
    if (!response?.ok) {
      const errorText = await response?.text()?.catch(() => 'Unknown error');
      return { success: false, error: `${response?.status}: ${errorText}` };
    }
    const data = await response?.json();
    return { success: true, data };
  } catch (err) {
    return { success: false, error: err?.message || 'Network error' };
  }
};

export const billingService = {
  async getBillingHistory(userId, options = {}) {
    if (!userId) return { data: [], error: 'User ID required' };
    try {
      if (!supabase) return { data: [], error: 'Database not available' };
      let query = supabase
        ?.from('orders')
        ?.select(`
          *,
          order_items (
            *,
            subscription_plans (name, amount)
          )
        `)
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false });
      if (options?.limit) query = query?.limit(options?.limit);
      const { data, error } = await query;
      if (error) {
        console.warn('Error fetching billing history:', error?.message);
        return { data: [], error: error?.message };
      }
      return { data: data || [], error: null };
    } catch (err) {
      console.error('Error in getBillingHistory:', err);
      return { data: [], error: err?.message };
    }
  },

  async getInvoice(orderId) {
    if (!orderId) return { data: null, error: 'Order ID required' };
    try {
      if (!supabase) return { data: null, error: 'Database not available' };
      const { data, error } = await supabase
        ?.from('orders')
        ?.select(`
          *,
          order_items (
            *,
            subscription_plans (name, amount, billing_interval)
          )
        `)
        ?.eq('id', orderId)
        ?.single();
      if (error) {
        if (error?.code === 'PGRST116') return { data: null, error: null };
        throw error;
      }
      return { data, error: null };
    } catch (err) {
      console.error('Error fetching invoice:', err);
      return { data: null, error: err?.message };
    }
  },

  async getPaymentMethods(userId) {
    if (!userId) return { data: [], error: 'User ID required' };
    try {
      if (!supabase) return { data: [], error: 'Database not available' };
      const { data, error } = await supabase
        ?.from('payment_methods')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.eq('is_active', true)
        ?.order('is_default', { ascending: false });
      if (error) {
        console.warn('Error fetching payment methods:', error?.message);
        return { data: [], error: error?.message };
      }
      return { data: data || [], error: null };
    } catch (err) {
      console.error('Error in getPaymentMethods:', err);
      return { data: [], error: err?.message };
    }
  },

  async setDefaultPaymentMethod(userId, paymentMethodId) {
    if (!userId || !paymentMethodId) return { success: false, error: 'User ID and payment method ID required' };
    return callEdgeFunction('setup-payment-method', {
      action: 'set_default',
      userId,
      paymentMethodId
    });
  },

  async removePaymentMethod(userId, paymentMethodId) {
    if (!userId || !paymentMethodId) return { success: false, error: 'User ID and payment method ID required' };
    return callEdgeFunction('setup-payment-method', {
      action: 'delete_payment_method',
      userId,
      paymentMethodId
    });
  },

  async getBillingOverview(userId) {
    if (!userId) return { data: null, error: 'User ID required' };
    try {
      if (!supabase) return { data: null, error: 'Database not available' };
      const [subscriptionResult, ordersResult] = await Promise.all([
        supabase?.from('subscriptions')?.select('*, subscription_plans(name, amount)')?.eq('user_id', userId)?.eq('status', 'active')?.single(),
        supabase?.from('orders')?.select('id, total_amount, status, created_at')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(5)
      ]);
      return {
        data: {
          currentSubscription: subscriptionResult?.data || null,
          recentOrders: ordersResult?.data || []
        },
        error: null
      };
    } catch (err) {
      console.error('Error in getBillingOverview:', err);
      return { data: null, error: err?.message };
    }
  }
};

export default billingService;